import { WebSocketMessageType, type INotificationMessage } from "@src/models";
import { getAccessToken } from "@utils/tokenStore";

const BASE_URL = import.meta.env.VITE_EXTRACTION_NOTIFICATION_URL;
if (!BASE_URL) {
  throw new Error(
    "VITE_EXTRACTION_NOTIFICATION_URL is not set in your .env file.",
  );
}

interface WebSocketCallbacks {
  onMessage?: (message: INotificationMessage) => void;
  onError?: (error: Event) => void;
  onClose?: (event: CloseEvent) => void;
  onOpen?: () => void;
  onAuthenticated?: () => void;
  onAuthenticationFailed?: (reason: string) => void;
}

class NotificationApi {
  /**
   * How long to wait for the backend's verdict on the auth frame. The socket used
   * to have no timeout anywhere, so a connection that opened and then said nothing
   * blocked the upload that was waiting on it indefinitely.
   */
  private static readonly AUTH_TIMEOUT_MS = 10_000;

  private websockets: Map<string, WebSocket> = new Map();
  private isAuthenticated: Map<string, boolean> = new Map();
  private heartbeatIntervals: Map<string, number> = new Map();
  private authPromises: Map<
    string,
    { resolve: () => void; reject: (reason: Error) => void }
  > = new Map();
  private authTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map();

  /**
   * Connect to WebSocket and authenticate. Returns a Promise that resolves
   * once authentication succeeds, so callers can wait before triggering
   * backend processing (e.g. file upload).
   */
  /**
   * Reject a pending auth promise, if there is one.
   *
   * Every way this socket can end without a verdict used to leave the promise
   * pending forever: an unhandled frame type, a socket error, a silent close, or
   * simply no answer. The upload path awaits it, so the file was never sent and
   * the PENDING process it belonged to was orphaned with no error shown.
   */
  private failPendingAuth(processId: string, reason: string): void {
    const pending = this.authPromises.get(processId);
    if (!pending) {
      return;
    }
    this.authPromises.delete(processId);
    this.clearAuthTimeout(processId);
    pending.reject(new Error(reason));
  }

  private clearAuthTimeout(processId: string): void {
    const timeout = this.authTimeouts.get(processId);
    if (timeout !== undefined) {
      clearTimeout(timeout);
      this.authTimeouts.delete(processId);
    }
  }

  public async connectAndWaitForAuth(
    doctypeCode: string,
    processId: string,
    callbacks: WebSocketCallbacks = {},
  ): Promise<void> {
    // BeeHive refreshes an expired token here. Reading the synchronous cache
    // replayed the token captured when the remote mounted, even though REST
    // calls made at the same time were already using a refreshed token.
    const accessToken = await getAccessToken();
    if (!accessToken) {
      throw new Error("Cannot connect: Access token not found.");
    }

    // Remove a previous socket before registering the new pending promise;
    // disconnect rejects any promise currently stored for this process.
    if (this.websockets.has(processId)) {
      this.disconnect(processId);
    }

    return new Promise<void>((resolve, reject) => {
      this.authPromises.set(processId, { resolve, reject });

      // A verdict must arrive, or the caller has to be able to give up. Without
      // this, a socket that opens and then says nothing blocks the upload with no
      // timeout anywhere in the chain.
      this.authTimeouts.set(
        processId,
        setTimeout(() => {
          this.failPendingAuth(
            processId,
            "Notification service did not confirm authentication in time",
          );
          this.disconnect(processId);
        }, NotificationApi.AUTH_TIMEOUT_MS),
      );

      this.openConnection(doctypeCode, processId, accessToken, {
        ...callbacks,
        onAuthenticated: () => {
          this.authPromises.delete(processId);
          this.clearAuthTimeout(processId);
          callbacks.onAuthenticated?.();
          resolve();
        },
        onAuthenticationFailed: (reason: string) => {
          this.authPromises.delete(processId);
          this.clearAuthTimeout(processId);
          callbacks.onAuthenticationFailed?.(reason);
          reject(new Error(reason));
        },
      });
    });
  }

  public connectToNotifications(
    doctypeCode: string,
    processId: string,
    callbacks: WebSocketCallbacks = {},
  ): void {
    void getAccessToken().then((accessToken) => {
      if (!accessToken) {
        const reason = "Cannot connect: Access token not found.";
        console.error(reason);
        callbacks.onAuthenticationFailed?.(reason);
        return;
      }

      if (this.websockets.has(processId)) {
        this.disconnect(processId);
      }
      this.openConnection(doctypeCode, processId, accessToken, callbacks);
    });
  }

  private openConnection(
    doctypeCode: string,
    processId: string,
    accessToken: string,
    callbacks: WebSocketCallbacks,
  ): void {
    const wsUrl = `${BASE_URL}?process_id=${encodeURIComponent(processId)}`;
    const websocket = new WebSocket(wsUrl);
    this.websockets.set(processId, websocket);
    this.isAuthenticated.set(processId, false);

    websocket.onopen = () => {
      callbacks.onOpen?.();
      this.authenticate(processId, doctypeCode, accessToken);
    };

    websocket.onmessage = (event) => {
      this.handleMessage(processId, event, callbacks);
    };

    websocket.onerror = (error) => {
      console.error(`WebSocket error for ${processId}:`, error);
      callbacks.onError?.(error);
    };

    websocket.onclose = (event) => {
      this.disconnect(processId, callbacks.onClose, event);
    };
  }

  private handleMessage(
    processId: string,
    event: MessageEvent,
    callbacks: WebSocketCallbacks,
  ) {
    try {
      const message: INotificationMessage = JSON.parse(event.data);
      switch (message.type) {
        case WebSocketMessageType.AuthenticationSuccess:
          this.isAuthenticated.set(processId, true);
          this.startHeartbeat(processId);
          callbacks.onAuthenticated?.();
          break;

        case WebSocketMessageType.AuthenticationFailed:
        case WebSocketMessageType.AuthenticationTimeout:
          this.isAuthenticated.set(processId, false);
          callbacks.onAuthenticationFailed?.(
            message.message || "Authentication failed",
          );
          this.disconnect(processId);
          break;

        case WebSocketMessageType.Pong:
          break;

        case WebSocketMessageType.StatusUpdate:
        case WebSocketMessageType.ConnectionEstablished:
        case WebSocketMessageType.Ping:
          // Authentication is persisted before its acknowledgement is sent.
          // A fast upload can therefore trigger a valid status update from a
          // different Lambda while this client is still waiting for
          // authentication_success. Deliver protocol/application frames and keep
          // waiting for the explicit auth verdict.
          break;

        case WebSocketMessageType.Error:
          // The backend used to answer an expired token with `type: "error"`,
          // so retain this as a legacy authentication failure while an auth verdict
          // is pending.
          if (this.authPromises.has(processId)) {
            this.failPendingAuth(
              processId,
              message.message || "Notification service reported an error",
            );
            this.disconnect(processId);
          }
          break;

        default:
          // Unknown frames do not prove that authentication failed. Keep the
          // bounded auth wait alive; AUTH_TIMEOUT_MS still prevents deadlocks.
          console.warn(
            `Ignoring unknown WebSocket message type for ${processId}:`,
            message.type,
          );
          break;
      }

      callbacks.onMessage?.(message);
    } catch (error) {
      console.error("Error parsing WebSocket message:", error);
    }
  }

  private authenticate(
    processId: string,
    doctypeCode: string,
    accessToken: string,
  ) {
    const websocket = this.websockets.get(processId);
    if (!websocket || websocket.readyState !== WebSocket.OPEN) {
      console.error(`WebSocket for ${processId} not ready for authentication.`);
      return;
    }

    const authMessage = {
      type: WebSocketMessageType.Authentication,
      token: accessToken,
      doctype_code: doctypeCode,
      timestamp: new Date().toISOString(),
    };

    websocket.send(JSON.stringify(authMessage));
  }

  private startHeartbeat(processId: string) {
    this.stopHeartbeat(processId);
    const interval = window.setInterval(() => {
      this.sendPing(processId);
    }, 25000);
    this.heartbeatIntervals.set(processId, interval);
  }

  private stopHeartbeat(processId: string) {
    const interval = this.heartbeatIntervals.get(processId);
    if (interval) {
      clearInterval(interval);
      this.heartbeatIntervals.delete(processId);
    }
  }

  public sendPing(processId: string): void {
    const websocket = this.websockets.get(processId);
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      websocket.send(
        JSON.stringify({
          type: WebSocketMessageType.Ping,
          timestamp: new Date().toISOString(),
        }),
      );
    }
  }

  public disconnect(
    processId: string,
    onClose?: (event: CloseEvent) => void,
    event?: CloseEvent,
  ): void {
    this.stopHeartbeat(processId);
    const websocket = this.websockets.get(processId);
    if (websocket) {
      websocket.onopen = null;
      websocket.onmessage = null;
      websocket.onerror = null;
      websocket.onclose = null;
      if (websocket.readyState < WebSocket.CLOSING) {
        websocket.close();
      }
    }

    this.websockets.delete(processId);
    this.isAuthenticated.delete(processId);
    // Closing while an auth promise is outstanding means it will never be
    // answered; the caller has to learn that rather than wait.
    this.failPendingAuth(processId, "Notification connection closed before authentication");

    if (event && onClose) {
      onClose(event);
    }
  }
}

export const notificationApi = new NotificationApi();
